/** @jsxImportSource preact */
import { h } from 'preact';
import { useState } from 'preact/hooks';
import { MultiSelectDropdown } from './MultiSelectDropdown';

export type StackByOption = 'coverage' | 'jurisdiction' | 'authority' | 'legislativeStatus' | 'activity';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterControlsProps {
  stackBy: StackByOption;
  onStackByChange: (value: StackByOption) => void;
  coverageFilter: string[];
  onCoverageFilterChange: (value: string[]) => void;
  jurisdictionFilter: string[];
  onJurisdictionFilterChange: (value: string[]) => void;
  authorityFilter: string[];
  onAuthorityFilterChange: (value: string[]) => void;
  legislativeStatusFilter: string[];
  onLegislativeStatusFilterChange: (value: string[]) => void;
  activityFilter: string[];
  onActivityFilterChange: (value: string[]) => void;
  appliesToFilter: string[];
  onAppliesToFilterChange: (value: string[]) => void;
  lifecycleStageFilter: string[];
  onLifecycleStageFilterChange: (value: string[]) => void;
  technicalScopeFilter: string[];
  onTechnicalScopeFilterChange: (value: string[]) => void;
  riskSubdomainFilter: string[];
  onRiskSubdomainFilterChange: (value: string[]) => void;
  riskSubdomainOptions: FilterOption[];
  sectorFilter: string[];
  onSectorFilterChange: (value: string[]) => void;
  sectorOptions: FilterOption[];
  jurisdictions: FilterOption[];
  authorities: FilterOption[];
  legislativeStatuses: FilterOption[];
  activities: FilterOption[];
  appliesToOptions: FilterOption[];
  lifecycleStageOptions: FilterOption[];
  technicalScopeOptions: FilterOption[];
  isMobile: boolean;
  documentCount?: number;
  onReset?: () => void;
  /** Title displayed above the filter bar. Defaults to "Risk Subdomain Coverage". */
  chartTitle?: string;
  /** Which stack-by buttons to show. Defaults to all. */
  stackByOptions?: StackByOption[];
  /** Whether to show the coverage filter dropdown. Defaults to true. */
  showCoverageFilter?: boolean;
  /** Filter dropdowns to hide (e.g. when their dimension is the y-axis). */
  hiddenFilters?: string[];
  /** Actor role selection (actors chart only) */
  actorRole?: string;
  onActorRoleChange?: (role: string) => void;
  actorRoleOptions?: { id: string; label: string }[];
}

const COVERAGE_OPTIONS: FilterOption[] = [
  { value: '3', label: 'Good coverage' },
  { value: '2', label: 'Minimal coverage' },
  { value: '1', label: 'No coverage' },
];

const STACK_BY_DISPLAY: Record<StackByOption, string> = {
  coverage: 'Coverage',
  jurisdiction: 'Jurisdiction',
  authority: 'Authority',
  legislativeStatus: 'Legislative status',
  activity: 'Most recent activity',
};

export function StackByButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={active ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={active ? `Data currently stacked by ${label}` : `Click to stack chart data by ${label}`}
      style={{
        border: active
          ? '1px solid var(--gc-button-bg-active, #e5e7eb)'
          : hovered
            ? '1px solid var(--gc-foreground, #171717)'
            : '1px solid var(--gc-control-border, #d1d5db)',
        background: active
          ? 'var(--gc-button-bg-active, #e5e7eb)'
          : 'transparent',
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: active ? 600 : 400,
        textDecoration: 'none',
        color: active
          ? 'var(--gc-button-text-active, #111827)'
          : hovered
            ? 'var(--gc-foreground, #171717)'
            : 'var(--gc-text-muted, #6b7280)',
        cursor: active ? 'default' : 'pointer',
        whiteSpace: 'nowrap' as const,
        borderRadius: 9,
        lineHeight: '14px',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
      }}
    >
      {label}
    </button>
  );
}

export function FilterControls({
  stackBy,
  onStackByChange,
  coverageFilter,
  onCoverageFilterChange,
  jurisdictionFilter,
  onJurisdictionFilterChange,
  authorityFilter,
  onAuthorityFilterChange,
  legislativeStatusFilter,
  onLegislativeStatusFilterChange,
  activityFilter,
  onActivityFilterChange,
  appliesToFilter,
  onAppliesToFilterChange,
  lifecycleStageFilter,
  onLifecycleStageFilterChange,
  technicalScopeFilter,
  onTechnicalScopeFilterChange,
  riskSubdomainFilter,
  onRiskSubdomainFilterChange,
  riskSubdomainOptions,
  sectorFilter,
  onSectorFilterChange,
  sectorOptions,
  jurisdictions,
  authorities,
  legislativeStatuses,
  activities,
  appliesToOptions,
  lifecycleStageOptions,
  technicalScopeOptions,
  isMobile,
  documentCount,
  onReset,
  chartTitle = 'Risk Subdomain Coverage',
  stackByOptions: visibleStackByOptions,
  showCoverageFilter = true,
  hiddenFilters = [],
  actorRole,
  onActorRoleChange,
  actorRoleOptions,
}: FilterControlsProps) {
  const filterLabelStyle = {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    whiteSpace: 'nowrap' as const,
  };

  const effectiveStackByOptions = visibleStackByOptions || (['coverage', 'jurisdiction', 'authority', 'legislativeStatus', 'activity'] as StackByOption[]);

  const handleStackByClick = (value: StackByOption) => {
    onStackByChange(value);
    // When switching to a non-coverage split, auto-filter to Good coverage (only when coverage is relevant)
    if (value !== 'coverage' && showCoverageFilter) {
      onCoverageFilterChange(['3']);
    }
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 600,
        marginBottom: 8,
        color: 'var(--gc-foreground, inherit)',
      }}>
        {chartTitle}
      </div>
      {actorRole !== undefined && onActorRoleChange && actorRoleOptions && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
          marginBottom: 6,
          fontSize: 11,
        }}>
          <span style={{
            fontSize: 11,
            color: 'var(--gc-text-muted, #6b7280)',
          }}>
            Role:
          </span>
          {actorRoleOptions.map((role) => (
            <StackByButton
              key={role.id}
              label={role.label}
              active={actorRole === role.id}
              onClick={() => onActorRoleChange(role.id)}
            />
          ))}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          rowGap: 6,
          fontSize: 11,
          maxWidth: '100%',
          overflowX: 'visible',
        }}
      >
        {/* Coverage */}
        {effectiveStackByOptions.includes('coverage') && (
          <div style={filterLabelStyle}>
            <StackByButton
              label="Coverage"
              active={stackBy === 'coverage'}
              onClick={() => handleStackByClick('coverage')}
            />
            {showCoverageFilter && (
              <MultiSelectDropdown
                options={COVERAGE_OPTIONS}
                selected={coverageFilter}
                onChange={onCoverageFilterChange}
                isMobile={isMobile}
              />
            )}
          </div>
        )}

        {/* Jurisdiction */}
        {effectiveStackByOptions.includes('jurisdiction') && (
          <div style={filterLabelStyle}>
            <StackByButton
              label="Jurisdiction"
              active={stackBy === 'jurisdiction'}
              onClick={() => handleStackByClick('jurisdiction')}
            />
            <MultiSelectDropdown
              options={jurisdictions}
              selected={jurisdictionFilter}
              onChange={onJurisdictionFilterChange}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Authority */}
        {effectiveStackByOptions.includes('authority') && (
          <div style={filterLabelStyle}>
            <StackByButton
              label="Authority"
              active={stackBy === 'authority'}
              onClick={() => handleStackByClick('authority')}
            />
            <MultiSelectDropdown
              options={authorities}
              selected={authorityFilter}
              onChange={onAuthorityFilterChange}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Legislative Status */}
        {effectiveStackByOptions.includes('legislativeStatus') && !hiddenFilters.includes('legislativeStatus') && (
          <div style={filterLabelStyle}>
            <StackByButton
              label="Legislative status"
              active={stackBy === 'legislativeStatus'}
              onClick={() => handleStackByClick('legislativeStatus')}
            />
            <MultiSelectDropdown
              options={legislativeStatuses}
              selected={legislativeStatusFilter}
              onChange={onLegislativeStatusFilterChange}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Most Recent Activity */}
        {effectiveStackByOptions.includes('activity') && (
          <div style={filterLabelStyle}>
            <StackByButton
              label="Most recent activity"
              active={stackBy === 'activity'}
              onClick={() => handleStackByClick('activity')}
            />
            <MultiSelectDropdown
              options={activities}
              selected={activityFilter}
              onChange={onActivityFilterChange}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Primarily applies to */}
        {!hiddenFilters.includes('appliesTo') && (
          <div style={filterLabelStyle}>
            <span style={{ fontSize: 11, color: 'var(--gc-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
              Primarily applies to
            </span>
            <MultiSelectDropdown
              options={appliesToOptions}
              selected={appliesToFilter}
              onChange={onAppliesToFilterChange}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* AI Lifecycle Stages */}
        {!hiddenFilters.includes('lifecycleStage') && (
          <div style={filterLabelStyle}>
            <span style={{ fontSize: 11, color: 'var(--gc-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
              Lifecycle stage
            </span>
            <MultiSelectDropdown
              options={lifecycleStageOptions}
              selected={lifecycleStageFilter}
              onChange={onLifecycleStageFilterChange}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Technical Scope */}
        {!hiddenFilters.includes('technicalScope') && (
          <div style={filterLabelStyle}>
            <span style={{ fontSize: 11, color: 'var(--gc-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
              Technical scope mentioned
            </span>
            <MultiSelectDropdown
              options={technicalScopeOptions}
              selected={technicalScopeFilter}
              onChange={onTechnicalScopeFilterChange}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* Risk Subdomain (cross-cutting filter) */}
        {!hiddenFilters.includes('riskSubdomain') && (
          <div style={filterLabelStyle}>
            <span style={{ fontSize: 11, color: 'var(--gc-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
              Risk subdomain
            </span>
            <MultiSelectDropdown
              options={riskSubdomainOptions}
              selected={riskSubdomainFilter}
              onChange={onRiskSubdomainFilterChange}
              isMobile={isMobile}
              infoText="Filter for documents with Good Coverage"
            />
          </div>
        )}

        {/* Sector (cross-cutting filter) */}
        {!hiddenFilters.includes('sector') && (
          <div style={filterLabelStyle}>
            <span style={{ fontSize: 11, color: 'var(--gc-text-muted, #6b7280)', whiteSpace: 'nowrap' }}>
              Sector
            </span>
            <MultiSelectDropdown
              options={sectorOptions}
              selected={sectorFilter}
              onChange={onSectorFilterChange}
              isMobile={isMobile}
              infoText="Filter for documents with Good Coverage"
            />
          </div>
        )}

        {/* Reset – only show when at least one filter is visible */}
        {onReset && (effectiveStackByOptions.length > 0 || showCoverageFilter || ['appliesTo', 'lifecycleStage', 'technicalScope', 'riskSubdomain', 'sector'].some(f => !hiddenFilters.includes(f))) && (
          <button
            type="button"
            onClick={onReset}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              fontSize: 11,
              fontWeight: 400,
              textDecoration: 'underline',
              color: 'var(--gc-text-muted, #6b7280)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            title="Reset all filters to defaults"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
